import type { HypertensionClassification, SubmitScanRequest } from '@/api/types'
import type { MindsetBloodPressure, MindsetFinalValues } from './mindset-types'

/**
 * Turns the vendor's per-vital result into the one shape this platform submits
 * (`SubmitScanRequest`), or null when there is nothing worth submitting.
 *
 * Two rules run through all of it.
 *
 * **`_MD` before `_GW`.** Every vital is emitted twice, by a clinical-grade model and a
 * general-wellness one, and which pair a run gets is the clinic's entitlement. Where both arrive the
 * clinical one is read; where only one does, that one is.
 *
 * **A non-positive value is a missing value.** The vendor reports 0 or a negative for a vital it
 * could not measure, exactly as FaceHeart does. A result with no pulse, or with no blood pressure of
 * either kind - neither a measured one nor the classifier's verdict - is not a submission, it is a
 * failed scan the patient is offered another go at.
 */
export function toSubmitRequestFromMindset(values: MindsetFinalValues | null | undefined): SubmitScanRequest | null {
  if (!values) {
    return null
  }

  const hr = mindsetPulseRate(values)
  const rr = mindsetRespirationRate(values)
  const spo2 = mindsetBloodOxygen(values)
  const bp = mindsetBloodPressure(values)

  if (!bp || !isPositive(hr)) {
    return null
  }

  return {
    ...bp,
    hr: Math.round(hr),
    ...(isPositive(spo2) ? { spo2: Math.round(spo2) } : {}),
    ...(isPositive(rr) ? { rr: Math.round(rr) } : {}),
  }
}

/**
 * The three vitals this vendor is read for, each its own exported function.
 *
 * **They are exported because a hybrid scan reads them one at a time.** The composite a hybrid scan
 * submits takes this vendor's pulse, respiration and blood oxygen beside the OTHER vendor's blood
 * pressure (docs/decisions.md D-37), so it needs the `_MD`-before-`_GW` rule and the
 * non-positive-is-missing rule per vital rather than a whole `SubmitScanRequest` that fails as a
 * unit. One source for those two rules, read by both callers, rather than a second mapper that
 * agrees with this one until somebody changes one of them.
 *
 * @returns The value, or null when the vendor measured nothing usable for it.
 */
export function mindsetPulseRate(values: MindsetFinalValues): number | null {
  return firstPositive(values.PR_MD?.hr, values.PR_GW?.hr)
}

/** Respiration rate, `_MD` before `_GW`. See `mindsetPulseRate`. */
export function mindsetRespirationRate(values: MindsetFinalValues): number | null {
  return firstPositive(values.RR_MD?.rr, values.RR_GW?.rr)
}

/** Blood oxygen, `_MD` before `_GW`. Note the vendor's capital O on the field. */
export function mindsetBloodOxygen(values: MindsetFinalValues): number | null {
  return firstPositive(values.SPO2_MD?.spO2, values.SPO2_GW?.spO2)
}

/**
 * What the vendor said about blood pressure, in whichever of its two shapes it answered: a measured
 * pressure from the ONNX regression, or the hypertension classifier's verdict with no numbers at
 * all. The two are submitted as what they are - a pressure, or a class and no pressure - and never
 * as one dressed up as the other (docs/decisions.md D-43).
 */
export type MindsetPressure =
  | { readonly sbp: number; readonly dbp: number }
  | { readonly hypertensionClassification: HypertensionClassification }

/** Blood pressure, `_MD` before `_GW`, in either shape the vendor's two model families produce. */
export function mindsetBloodPressure(values: MindsetFinalValues): MindsetPressure | null {
  return bloodPressure(values.BP_MD) ?? bloodPressure(values.BP_GW)
}

function bloodPressure(pressure: MindsetBloodPressure | undefined): MindsetPressure | null {
  if (!pressure) {
    return null
  }

  const sbp = pressure.BP_SYS
  const dbp = pressure.BP_DIA

  if (isPositive(sbp) && isPositive(dbp)) {
    return { sbp: Math.round(sbp), dbp: Math.round(dbp) }
  }

  const hypertensive = classifiedHypertensive(pressure)

  if (hypertensive === null) {
    return null
  }

  return { hypertensionClassification: hypertensive ? 'Hypertensive' : 'Normal' }
}

/**
 * The classifier's answer, from either of the two fields it is known to arrive in: the raw boolean,
 * or the string the vendor's own client derives from it.
 */
function classifiedHypertensive(pressure: MindsetBloodPressure): boolean | null {
  if (typeof pressure.hypertensive === 'boolean') {
    return pressure.hypertensive
  }
  if (typeof pressure.classification === 'string') {
    const classification = pressure.classification.toLowerCase()
    if (classification === 'hypertensive') return true
    if (classification === 'normal') return false
  }
  return null
}

function firstPositive(...values: readonly (number | null | undefined)[]): number | null {
  return values.find(isPositive) ?? null
}

function isPositive(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
}
